import {
  FallOutlined,
  InfoCircleOutlined,
  RiseOutlined,
} from '@ant-design/icons';
import { Col, Row, Tooltip } from 'antd';
import { Clock } from 'icons-new';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { EHDChange } from '../hooks/useEHDChangeQuery';

type EHDStatusWidgetProps = {
  data?: EHDChange[];
};

const ehdColor = {
  onSchedule: 'blue',
  delayed: 'yellow',
  faster: 'green',
};

const EHDStatusWidget = ({ data }: EHDStatusWidgetProps) => {
  const { t } = useTranslation();
  const totalEHDChanges = data?.reduce((acc, item) => acc + item.count, 0);
  const ehdData = useMemo(
    () => ({
      onSchedule: data?.filter((item) => item.status === 'ON_SCHEDULE')?.[0],
      delayed: data?.filter((item) => item.status === 'DELAYED')?.[0],
      faster: data?.filter((item) => item.status === 'FASTER')?.[0],
    }),
    [data],
  );

  const hasDaysChanged = (status: keyof typeof ehdData) => {
    return (
      ehdData[status]?.minChanges &&
      ehdData[status]?.maxChanges &&
      ehdData[status].minChanges > 0 &&
      ehdData[status].maxChanges > 0
    );
  };

  const addPlusMinusSign = (value?: number) => {
    if (value) {
      if (value > 0) return `+${value}`;
      if (value < 0) return `-${value}`;
    }
    return value;
  };

  const renderStatusTag = (status: keyof typeof ehdData) => (
    <>
      <span className={`tag ${ehdColor[status]}`}>
        {hasDaysChanged(status)
          ? `${addPlusMinusSign(ehdData[status]?.minChanges)} ${t('common.to')} ${addPlusMinusSign(
              ehdData[status]?.maxChanges,
            )} ${t('common.days')}`
          : '0 ' + t('common.days')}
      </span>
      {hasDaysChanged(status) ? (
        <Tooltip title={t('collectionOverview.status.changeInDuration')}>
          <InfoCircleOutlined className="info-icon" />
        </Tooltip>
      ) : null}
    </>
  );

  return (
    <Row className="base-card" gutter={[16, 16]}>
      <Col md={6} span={24}>
        <div className="ehd-first-card flex-vertical">
          <span className="text-large">
            {t('collectionOverview.ehdChangeWidgetTitle')}
          </span>
          <span className="text-display-large text-strong">
            {totalEHDChanges}
          </span>
        </div>
      </Col>
      <Col md={6} span={24}>
        <div className="ehd-status-card base-card flex-vertical">
          <div className="icon blue flex-center">
            <Clock />
          </div>
          <span>{t('collectionOverview.status.onSchedule')}</span>
          <span className="heading-3 text-strong">
            {ehdData.onSchedule?.count ?? 0}
          </span>
          <div className="flex-center">{renderStatusTag('onSchedule')}</div>
        </div>
      </Col>
      <Col md={6} span={12}>
        <div className="ehd-status-card base-card flex-vertical">
          <div className="icon yellow flex-center">
            <FallOutlined />
          </div>
          <span>{t('collectionOverview.status.delayed')}</span>
          <span className="heading-3 text-strong">
            {ehdData.delayed?.count ?? 0}
          </span>
          <div className="flex-center">{renderStatusTag('delayed')}</div>
        </div>
      </Col>
      <Col md={6} span={12}>
        <div className="ehd-status-card base-card flex-vertical">
          <div className="icon green flex-center">
            <RiseOutlined />
          </div>
          <span>{t('collectionOverview.status.faster')}</span>
          <span className="heading-3 text-strong">
            {ehdData.faster?.count ?? 0}
          </span>
          <div className="flex-center">{renderStatusTag('faster')}</div>
        </div>
      </Col>
    </Row>
  );
};

export default EHDStatusWidget;
